import React, { startTransition, useCallback, useState } from 'react';
import { gql, useMutation, useSuspenseFragment } from '@apollo/client';
import { MdClose } from 'react-icons/md';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { UpdateVariationTypeMutation, UpdateVariationTypeMutationVariables, UpdateVariationTypeValueMutation, UpdateVariationTypeValueMutationVariables, VariationsSection_AdFragment, UpdateVariationTypeValueMediasMutation, GenerateVariationsMutationVariables, GenerateVariationsMutation, UpdateVariationTypeValueMediasMutationVariables, LinkAdVariationToSupplierProductVariationMutation, UnlinkAdVariationFromSupplierProductVariationMutationVariables, LinkAdVariationToSupplierProductVariationMutationVariables, UnlinkAdVariationFromSupplierProductVariationMutation, DeleteVariationMutation, DeleteVariationMutationVariables, UpdateAdVariationMutation, UpdateAdVariationMutationVariables, VariationsSection_DeleteVariationTypeMutation, VariationsSection_DeleteVariationTypeMutationVariables } from '../../../codegen/graphql';
import { Toggle } from '../common/Toggle';
import { TextInput } from '../common/TextInput';
import Button from '../common/Button';
import { toast } from 'react-toastify';
import SupplierSelector from './SupplierSelector';
import { CopyButton } from '../AdListing/CopyButton';
import { AddAdVariationType } from './AddAdVariationType';
import { AddAdVariationTypeValue } from './AddAdVariationTypeValue';
import { getTranslation, TranslatedValue, Translations, useTranslationEditsMap } from '../common/TranslatedValue';
import { SortableTypeValue } from './SortableTypeValue';
import SetMarginOrTargetPrice from '../common/SetMarginOrTargetPrice';
import { useRequestManager } from '../common/RequestManager';


interface VariationsSectionProps {
  adData: VariationsSection_AdFragment;
  refetch: () => void;
  onAdDataChange: (data: any) => void;
  fieldsKeys?: string[] | null;
  itemsDefinitions?: any | null;
}

export function VariationsSection({ 
  adData, 
  refetch,
  onAdDataChange
}: VariationsSectionProps) {
  const { trackRequest } = useRequestManager();
  const translationEdits = useTranslationEditsMap();

  const {
    id: adId,
    variationTypes,
    variations
  } = adData;

  // Remove unused imports and variables
  // Add new state for filters
  const [filterType, setFilterType] = useState<string>('');
  const [filterValue, setFilterValue] = useState<string>('');
  const [activeFilters, setActiveFilters] = useState<Array<{ typeId: string; valueId: string }>>([]);


  const [skuSearch, setSkuSearch] = useState<string>('');

  // DND-Kit sensors
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 8,
    },
  });

  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 200,
      tolerance: 5,
    },
  });

  const sensors = useSensors(
    mouseSensor,
    touchSensor,
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Add new state for selected variations and bulk edit values
  const [selectedVariations, setSelectedVariations] = useState<string[]>([]);

  // Removed unused mutations
  const [updateAdVariation] = useMutation<UpdateAdVariationMutation, UpdateAdVariationMutationVariables>(UPDATE_AD_VARIATION);
  const [updateVariationType] = useMutation<UpdateVariationTypeMutation, UpdateVariationTypeMutationVariables>(UPDATE_VARIATION_TYPE);
  const [deleteVariationType] = useMutation<VariationsSection_DeleteVariationTypeMutation, VariationsSection_DeleteVariationTypeMutationVariables>(DELETE_VARIATION_TYPE);
  const [updateVariationTypeValue] = useMutation<UpdateVariationTypeValueMutation, UpdateVariationTypeValueMutationVariables>(UPDATE_VARIATION_TYPE_VALUE, {
    refetchQueries: ['AdForm_GetAd']
  });
  const [generateVariations] = useMutation<GenerateVariationsMutation, GenerateVariationsMutationVariables>(GENERATE_VARIATIONS);
  const [updateVariationTypeValueMedias] = useMutation<UpdateVariationTypeValueMediasMutation, UpdateVariationTypeValueMediasMutationVariables>(UPDATE_VARIATION_TYPE_VALUE_MEDIAS);

  const [supplierSelectorState, setSupplierSelectorState] = useState<{
    open: boolean;
    variationId: string;
    title: React.ReactNode;
    selectedVariations: Array<{
      sku: string;
      supplierProductId: string;
    }>;
  }>({
    open: false,
    variationId: '',
    title: '',
    selectedVariations: [],
  });

  // Modal states
  const [addTypeModalState, setAddTypeModalState] = useState<{
    isOpen: boolean;
    typeId?: string;
    typeName?: string;
  }>({
    isOpen: false,
  });

  const [addTypeValueModalState, setAddTypeValueModalState] = useState<{
    isOpen: boolean;
    typeId: string;
    typeValueId?: string;
    typeValueName?: string;
  }>({
    isOpen: false,
    typeId: '',
  });


  const handleOpenAddTypeModal = useCallback((typeId?: string, typeName?: string) => {
    setAddTypeModalState({
      isOpen: true,
      typeId,
      typeName,
    });
  }, []);

  const handleCloseAddTypeModal = useCallback(() => {
    setAddTypeModalState({
      isOpen: false,
    });
  }, []);

  const handleOpenAddTypeValueModal = useCallback((typeId: string, typeValueId?: string, typeValueName?: string) => {
    setAddTypeValueModalState({
      isOpen: true,
      typeId,
      typeValueId,
      typeValueName,
    });
  }, []);

  const handleCloseAddTypeValueModal = useCallback(() => {
    setAddTypeValueModalState({
      isOpen: false,
      typeId: '',
    });
  }, []);

  const handleChangeType = useCallback(async (typeId: string, value: Translations) => {
    await trackRequest(
      updateVariationType({ variables: { id: typeId, input: { type: value as any } } }), {
        title: `Modification du type "${getTranslation(value)}"`,
        isBlocking: false,
      }
    );
  }, []);

  const handleChangeTypeValue = useCallback(async (_typeId: string, typeValueId: string, value: Translations) => {
    await trackRequest(
      updateVariationTypeValue({ variables: { id: typeValueId, input: { value: value as any } } }), {
        title: `Modification du type valeur "${getTranslation(value)}"`,
        isBlocking: false,
      }
    );
  }, []);


  const handleImageSelection = useCallback(async (
    selectedImages: VariationsSection_AdFragment['medias'],
    variationTypeValueId: string
  ) => {
    await trackRequest(
      updateVariationTypeValueMedias({ variables: { id: variationTypeValueId, input: { mediaIds: selectedImages.map((img: VariationsSection_AdFragment['medias'][0]) => img.id) } } }), {
        title: `Modification des images du type valeur`,
        isBlocking: false,
      }
    );
  }, []);

  // Handle drag end for sorting type values
  const handleDragEnd = useCallback(async (event: DragEndEvent, typeId: string) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const type = variationTypes.find(t => t.id === typeId);
    if (!type) return;

    const sortedValues = type.typeValues.slice().sort((a, b) => (a.order || 0) - (b.order || 0));
    const oldIndex = sortedValues.findIndex(value => value.id === active.id);
    const newIndex = sortedValues.findIndex(value => value.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Create new order for all items
    const reorderedValues = [...sortedValues];
    const [movedValue] = reorderedValues.splice(oldIndex, 1);
    reorderedValues.splice(newIndex, 0, movedValue);

    // Update orders for all items
    await trackRequest(
      Promise.all(reorderedValues.map((value, index) => 
        updateVariationTypeValue({ variables: { id: value.id, input: { order: index } } })
      )
    ), {
      title: `Modification de l'ordre du type valeur`,
      isBlocking: false,
    })
  }, [variationTypes]);

  // Add new function to handle filter addition
  const handleAddFilter = (typeId?: string, valueId?: string) => {
    const filterTypeToUse = typeId || filterType;
    const filterValueToUse = valueId || filterValue;
    
    if (!filterTypeToUse || !filterValueToUse) return;
    
    if (!activeFilters.some(f => f.typeId === filterTypeToUse && f.valueId === filterValueToUse)) {
      setActiveFilters([...activeFilters, { typeId: filterTypeToUse, valueId: filterValueToUse }]);
    }
    
    if (!typeId && !valueId) {
      setFilterType('');
      setFilterValue('');
    }
  };

  // Add new function to handle filter removal
  const handleRemoveFilter = (filterToRemove: { typeId: string; valueId: string }) => {
    setActiveFilters(activeFilters.filter(
      f => !(f.typeId === filterToRemove.typeId && f.valueId === filterToRemove.valueId)
    ));
  };

  // Add new function to filter variations
  const filteredVariations = variations.filter(variation => {
    if (skuSearch && variation.id !== skuSearch) {
      return false;
    }

    if (activeFilters.length === 0) return true;
    
    return activeFilters.every(filter => {
      const matchingValue = variation.variationTypeValues.find(vtv => 
        vtv.type?.id === filter.typeId && vtv.id === filter.valueId
      );
      return !!matchingValue;
    });
  });


  const handleBulkDelete = async () => {
    // if (!confirm('Voulez-vous vraiment supprimer les variations sélectionnées ?')) {
    //   return;
    // }

    // try {
    //   await Promise.all(
    //     selectedVariations.map((variationId) =>
    //       deleteVariation({
    //         variables: {
    //           id: variationId
    //         }
    //       })
    //     )
    //   );
    //   setSelectedVariations([]);
    //   startTransition(() => {
    //     refetch();
    //   })
    // } catch (error) {
    //   console.error('Error deleting variations in bulk:', error);
    // }
  };

  // Add function to toggle all selections
  const toggleAllSelections = (checked: boolean) => {
    if (checked) {
      setSelectedVariations(filteredVariations.map(v => v.id));
    } else {
      setSelectedVariations([]);
    }
  };

  const [link_AdVariation_SupplierProductVariation] = useMutation<
    LinkAdVariationToSupplierProductVariationMutation,
    LinkAdVariationToSupplierProductVariationMutationVariables
  >(LINK_AD_VARIATION_TO_SUPPLIER_PRODUCT_VARIATION);
  const [unlink_AdVariation_SupplierProductVariation] = useMutation<
    UnlinkAdVariationFromSupplierProductVariationMutation,
    UnlinkAdVariationFromSupplierProductVariationMutationVariables
  >(UNLINK_AD_VARIATION_FROM_SUPPLIER_PRODUCT_VARIATION);

  // Removed unused mutations for type value linking

  const handleSupplierSelectorComplete = async (selectedVariations: Array<{
    sku: string;
    supplierProductId: string;
  }>) => {
    const adVariation = filteredVariations.find((v) => v.id === supplierSelectorState.variationId)

    if (!adVariation) {
      return;
    }

    const supplierVariationsToUnlink = adVariation?.supplierProductVariations.filter((v) => (
      !selectedVariations.some((sv) => (
        sv.sku === v.sku &&
        sv.supplierProductId === v.supplierProductId
      ))
    ))
    const supplierVariationsToLink = selectedVariations.filter((v) => (
      !adVariation?.supplierProductVariations.some((sv) => (
        sv.sku === v.sku &&
        sv.supplierProductId === v.supplierProductId
      ))
    ))


    const promises = []
    if (supplierVariationsToLink.length > 0) {
      promises.push(link_AdVariation_SupplierProductVariation({ variables: {
        adVariationId: adVariation.id,
        supplierProductVariationKeys: supplierVariationsToLink.map((v) => ({
          sku: v.sku,
          supplierProductId: v.supplierProductId
        }))
      } }))
    }

    if (supplierVariationsToUnlink.length > 0) {
      promises.push(unlink_AdVariation_SupplierProductVariation({ variables: {
        adVariationId: adVariation.id,
        supplierProductVariationKeys: supplierVariationsToUnlink.map((v) => ({
          sku: v.sku,
          supplierProductId: v.supplierProductId
        }))
      } }))
    }

    await Promise.all(promises)

    startTransition(() => {
      refetch();
    })

    setSupplierSelectorState({
      open: false,
      variationId: '',
      title: '',
      selectedVariations: []
    })

    toast.success('Fournisseurs liés avec succès');
  }

  const handleDeleteValue = async (value: any) => {
    if (!confirm(`Voulez-vous vraiment supprimer la valeur "${getTranslation(value.value)}" ?`)) {
      return;
    }

    try {
      // You'll need to implement the delete variation type value mutation
      // For now, we'll just show a toast
      toast.info('Fonctionnalité de suppression de valeur à implémenter');
    } catch (error: any) {
      toast.error('Une erreur est survenue: ' + error.message);
    }
  };

  const handleDeleteType = async (typeId: string) => {
    if (!confirm(`Voulez-vous vraiment supprimer le type "${typeId}" ?`)) {
      return;
    }

    try {
      await deleteVariationType({ variables: { id: typeId } });
    } catch (error: any) {
      toast.error('Une erreur est survenue: ' + error.message);
    }
  };

  const handleUpdateAdVariation = useCallback(async (variationId: string, data: UpdateAdVariationMutationVariables['input']) => {
    await trackRequest(
      updateAdVariation({ variables: { id: variationId, input: data } }), {
        title: `Modification de la variation "${variationId}"`,
        isBlocking: false,
      }
    );
  }, []);
  return (
    <div className="bg-white shadow-sm rounded-lg p-6">
      <h2 className="text-lg font-medium text-gray-900 mb-4">Types et valeurs</h2>

      {/* <Button onClick={importFranceMenShoeSize} preset="blue" size="sm">
        {selectedType
          ? 'Convertir vers les tailles de chaussures CN > France Homme'
          : 'Importer les tailles de chaussures CN > France Hommme'}
      </Button> */}

      <div className="flex flex-row gap-2">
        <div style={{ width: 700 }}>
          {/* Types and Values Section */}
          <div className="flex items-center mb-4">
            <Button
              onClick={() => handleOpenAddTypeModal()}
              size="sm"
              preset="white"
              className="mr-2"
            >
              Ajouter un type
            </Button>
          </div>

          {/* Types and Values */}
          <div className="space-y-4">
            {variationTypes.map((type) => (
              <div key={type.id} className="border rounded-lg overflow-hidden">
                {/* Type Header */}
                <div className="bg-gray-50 px-4 py-3 flex items-center gap-4">
                  
                  <TranslatedValue
                    translations={translationEdits.value(`type:${type.id}`, type.type as unknown as Translations)}
                    renderAs="input"
                    size="lg"
                    className="font-bold"
                    onEditTranslation={(updates) => handleChangeType(
                      type.id,
                      translationEdits.edit(`type:${type.id}`, type.type as unknown as Translations, updates),
                    )}
                  />
                  <Toggle
                    className="flex items-center"
                    label="Avec images"
                    value={type.id === adData.typeIdUsedForImages}
                    onChange={(value) => {
                      if (value) {
                        onAdDataChange({
                          typeIdUsedForImages: type.id
                        });
                      } else {
                        onAdDataChange({
                          typeIdUsedForImages: null
                        });
                      }
                    }}
                  />
                  <Button
                    preset="danger-light"
                    onClick={() => handleDeleteType(type.id)}
                  >
                    <MdClose size={20} />
                  </Button>
                  <div className="flex-1" />
                  <Button
                    preset="white"
                    onClick={() => handleOpenAddTypeValueModal(type.id)}
                    size="sm"
                  >
                    Ajouter une valeur
                  </Button>
                </div>

                {/* Type Values */}
                {/* Grid Header */}
                <div 
                  className="grid grid-cols-12 gap-2 px-2 py-2 bg-gray-50 border-b border-gray-200"
                >
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider col-span-1">
                    {/* Drag handle space */}
                  </div>
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider col-span-5">
                    Valeur
                  </div>
                  {type.id === adData.typeIdUsedForImages && (
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider col-span-5">
                      Images
                    </div>
                  )}
                  <div className={`text-xs font-medium text-gray-500 uppercase tracking-wide ${type.id === adData.typeIdUsedForImages ? 'col-span-1' : 'col-span-6'}`}>
                    Actions
                  </div>
                </div>
                
                {/* Sortable Grid Rows */}
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(event) => handleDragEnd(event, type.id)}
                >
                  <SortableContext 
                    items={type.typeValues.slice().sort((a, b) => (a.order || 0) - (b.order || 0)).map(value => value.id)} 
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="divide-y divide-gray-200">
                      {type.typeValues.slice().sort((a, b) => (a.order || 0) - (b.order || 0)).map((value) => (
                        <SortableTypeValue
                          key={value.id}
                          value={value}
                          type={type}
                          adData={adData}
                          activeFilters={activeFilters}
                          onChangeTypeValue={handleChangeTypeValue}
                          onImageSelection={handleImageSelection}
                          onAddFilter={handleAddFilter}
                          onRemoveFilter={handleRemoveFilter}
                          onDeleteValue={handleDeleteValue}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            ))}
          </div>
        </div>

        <div className="flex-1">
          {/* Variations List */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium">
              {filteredVariations.length} variations
            </h3>
            <Button
              preset="primary"
              onClick={() => {
                if (!confirm('Voulez-vous vraiment générer toutes les variations ?')) {
                  return;
                }

                generateVariations({ variables: { adId } })
              }}
            >
              Générer toutes les variations
            </Button>
          </div>

          {/* <p className={`mb-4 text-md text-gray-500 ${countLeboncoinListings >= 15 ? 'text-red-600' : 'text-green-600'}`}>
            Nombre d'annonces leboncoin crées : {countLeboncoinListings}
          </p> */}


          {/* Filter Section */}
          <div className="mb-2 p-2 bg-gray-100 rounded-lg">
            <div className="flex gap-4 mb-2">
              <select
                value={filterType}
                onChange={(e) => {
                  setFilterType(e.target.value);
                  setFilterValue('');
                }}
                className="rounded-md border-gray-300 shadow-xs focus:border-blue-500 focus:ring-blue-500 px-2 py-1"
              >
                <option value="">-- Type</option>
                {variationTypes.map((type) => (
                  <option key={type.id} value={type.id}>{getTranslation(type.type as unknown as Translations)}</option>
                ))}
              </select>
              
              <select
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
                disabled={!filterType}
                className="rounded-md border-gray-300 shadow-xs focus:border-blue-500 focus:ring-blue-500 px-2 py-1"
              >
                <option value="">-- Valeur</option>
                {filterType && variationTypes
                  .find(t => t.id === filterType)
                  ?.typeValues.map((value) => (
                    <option key={value.id} value={value.id}>{getTranslation(value.value as unknown as Translations)}</option>
                  ))}
              </select>
              
              <Button
                onClick={() => handleAddFilter(filterType, filterValue)}
                disabled={!filterType || !filterValue}
                size="sm"
              >
                Ajouter un filtre
              </Button>

              <TextInput
                value={skuSearch}
                onChange={(e) => setSkuSearch(e.target.value)}
                placeholder="Rechercher par SKU"
              />
            </div>

            {/* Active Filters Display */}
            {activeFilters.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {activeFilters.map((filter, index) => {
                  const type = variationTypes.find(t => t.id === filter.typeId);
                  const value = type?.typeValues.find(v => v.id === filter.valueId);
                  return (
                  <div
                    key={index}
                    className="flex items-center gap-2 px-3 py-1 bg-blue-100 rounded-md border border-blue-200"
                  >
                    <span className="text-sm text-blue-800 flex items-center gap-1">
                      <TranslatedValue
                        translations={translationEdits.value(`type:${type?.id ?? ''}`, type?.type as unknown as Translations)}
                        renderAs="text"
                        onEditTranslation={(updates) => {
                          if (!type) return;
                          handleChangeType(
                            type.id,
                            translationEdits.edit(`type:${type.id}`, type.type as unknown as Translations, updates),
                          );
                        }}
                      />
                      :
                      <TranslatedValue
                        translations={translationEdits.value(`value:${value?.id ?? ''}`, value?.value as unknown as Translations)}
                        renderAs="text"
                        onEditTranslation={(updates) => {
                          if (!type || !value) return;
                          handleChangeTypeValue(
                            type.id,
                            value.id,
                            translationEdits.edit(`value:${value.id}`, value.value as unknown as Translations, updates),
                          );
                        }}
                      />
                    </span>
                    <button
                      onClick={() => handleRemoveFilter(filter)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <MdClose size={20} />
                    </button>
                  </div>
                  );
                })}
              </div>
            )}

            {selectedVariations.length > 0 && (
              <div className="flex items-center gap-4 mt-2 bg-gray-50 rounded-lg">
                <span className="text-md text-gray-700">
                  {selectedVariations.length} variation(s) selectionnées
                </span>
              </div>
            )}
          </div>

          <div className="overflow-x-auto">
            {/* Grid Header */}
            <div 
              className="grid gap-2 px-3 py-3 bg-gray-50 border-b border-gray-200"
              style={{
                gridTemplateColumns: `repeat(${6 + variationTypes.length * 1}, minmax(0, 1fr))`
              }}
            >
              <div className="col-span-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Supplier
              </div>
              <div className="col-span-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
                <Toggle
                  label=""
                  value={selectedVariations.length === filteredVariations.length}
                  onChange={(value) => toggleAllSelections(value)}
                />
              </div>
              <div className="col-span-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Image
              </div>
              {variationTypes.map(type => (
                <div
                  key={type.id}
                  className="col-span-1 text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  <TranslatedValue
                    translations={translationEdits.value(`type:${type.id}`, type.type as unknown as Translations)}
                    renderAs="text"
                    onEditTranslation={(updates) => handleChangeType(
                      type.id,
                      translationEdits.edit(`type:${type.id}`, type.type as unknown as Translations, updates),
                    )}
                  />
                </div>
              ))}
              <div className="col-span-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Margin or target price
              </div>
            </div>
            
            {/* Grid Rows */}
            <div className="divide-y divide-gray-200">
              {filteredVariations.map((variation) => (
                <div 
                  key={variation.id}
                  className={`grid gap-2 px-3 py-3 hover:bg-gray-50 ${
                    selectedVariations.includes(variation.id) ? 'bg-blue-50' : ''
                  }`}
                  style={{ 
                    position: 'relative',
                    gridTemplateColumns: `repeat(${6 + variationTypes.length * 1}, minmax(0, 1fr))`
                  }}
                >
                  <div className="text-sm text-gray-900">
                    {variation.supplierProductVariations.length} suppliers<br/>
                    <Button
                      onClick={() => setSupplierSelectorState({
                        open: true,
                        variationId: variation.id,
                        title: <div className="flex flex-row gap-2 items-center">
                          <img src={variation.variationTypeValues.find(
                            v => v.type?.id === adData.typeIdUsedForImages
                          )?.medias[0]?.url}
                          alt="Image" className="w-20 h-20 object-contain rounded-md" />
                          <span>Pour : </span>
                          <span className="flex flex-wrap gap-1">
                            {variation.variationTypeValues.map(v => (
                              <TranslatedValue
                                key={v.id}
                                translations={translationEdits.value(`value:${v.id}`, v.value as unknown as Translations)}
                                renderAs="text"
                                onEditTranslation={(updates) => handleChangeTypeValue(
                                  v.type.id,
                                  v.id,
                                  translationEdits.edit(`value:${v.id}`, v.value as unknown as Translations, updates),
                                )}
                              />
                            ))}
                          </span>
                        </div>,
                        selectedVariations: variation.supplierProductVariations.map((supplierProductVariation) => ({
                          sku: supplierProductVariation.sku,
                          supplierProductId: supplierProductVariation.supplierProductId,
                        }))
                      })}
                      size="sm"
                      preset="white"
                    >
                      Lier
                    </Button>
                  </div>
                  <div className="col-span-1 flex flex-col justify-center text-sm text-gray-900">
                    <Toggle
                      label=""
                      value={selectedVariations.includes(variation.id)}
                      onChange={(value) => {
                        if (value) {
                          setSelectedVariations([...selectedVariations, variation.id]);
                        } else {
                          setSelectedVariations(selectedVariations.filter(id => id !== variation.id));
                        }
                      }}
                    />
                    <CopyButton
                      text={variation.id}
                      label="Copier l'ID"
                      tooltip={variation.id}
                    />
                  </div>
                  <div className="col-span-1 text-sm text-gray-900">
                    <img
                      src={variation.variationTypeValues.find(
                        v => v.type?.id === adData.typeIdUsedForImages
                      )?.medias[0]?.url}
                      className="w-20 h-20 object-contain rounded-md"
                    />
                  </div>
                  {variationTypes.map(type => (
                    <div
                      key={type.id}
                      className="col-span-1 text-md text-gray-900"
                    >
                      <TranslatedValue
                        translations={translationEdits.value(
                          `value:${variation.variationTypeValues.find(v => v.type?.id === type.id)?.id ?? ''}`,
                          variation.variationTypeValues.find(v => v.type?.id === type.id)?.value as unknown as Translations,
                        )}
                        renderAs="text"
                        onEditTranslation={(updates) => {
                          const typeValue = variation.variationTypeValues.find(v => v.type?.id === type.id);
                          if (!typeValue) return;
                          handleChangeTypeValue(
                            type.id,
                            typeValue.id,
                            translationEdits.edit(`value:${typeValue.id}`, typeValue.value as unknown as Translations, updates),
                          );
                        }}
                      />
                      {/* {(
                        <select
                          value={variation.variationTypeValues.find(
                            v => v.type?.id === type.id
                          )?.id}
                          onChange={(e) => {
                            const currentTypeValue = variation.variationTypeValues.find(v => v.type?.id === type.id);
                            if (currentTypeValue) {
                              unlink_AdVariation_TypeValue({
                                variables: {
                                  adVariationId: variation.id,
                                  typeValueIds: [currentTypeValue.id]
                                }
                              })
                            }
                            
                            const newTypeValueId = e.target.value;
                            link_AdVariation_TypeValue({
                              variables: {
                                adVariationId: variation.id,
                                typeValueIds: [newTypeValueId]
                              }
                            })
                          }}
                        >
                          <option key="empty" value="">--</option>
                          {type.typeValues.map((value) => (
                            <option
                              key={value.id}
                              value={value.id}
                            >
                              {getTranslation(value.value as unknown as Translations)}
                            </option>
                          ))}
                        </select>
                      )} */}
                    </div>
                  ))}

                  <div className="col-span-3 text-md text-gray-900">
                    <SetMarginOrTargetPrice
                      data={{
                        listing: null,
                        listingConfiguration: null,
                        variation: variation,
                        ad: adData,
                      }}
                      setForKey={`variation-${variation.id}`}
                      onUpdate={(data) => {
                        handleUpdateAdVariation(variation.id, data);
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {supplierSelectorState.open && (
        <SupplierSelector
          adId={adData.id}
          title={supplierSelectorState.title}
          initialSelectedVariations={supplierSelectorState.selectedVariations}
          onComplete={handleSupplierSelectorComplete}
          onCancel={() => setSupplierSelectorState({ ...supplierSelectorState, open: false })}
        />
      )}

      {/* Add Type Modal */}
      {addTypeModalState.isOpen && <AddAdVariationType
        isOpen={addTypeModalState.isOpen}
        onClose={handleCloseAddTypeModal}
        adId={adData.id}
        typeId={addTypeModalState.typeId}
        typeName={addTypeModalState.typeName}
        onSuccess={refetch}
      />}

      {/* Add Type Value Modal */}
      {addTypeValueModalState.isOpen && <AddAdVariationTypeValue
        isOpen={addTypeValueModalState.isOpen}
        onClose={handleCloseAddTypeValueModal}
        adId={adData.id}
        typeId={addTypeValueModalState.typeId}
        typeValueId={addTypeValueModalState.typeValueId}
        typeValueName={addTypeValueModalState.typeValueName}
        onSuccess={refetch}
      />}
    </div>
  );
}



export const UPDATE_VARIATION_TYPE_VALUE_MEDIAS = gql`
  mutation UpdateVariationTypeValueMedias($id: ID!, $input: UpdateVariationTypeValueMediasInput!) {
    updateVariationTypeValueMedias(id: $id, input: $input) {
      id
      medias {
        id
        url
      }
    }
  }
`;

export const DELETE_VARIATION = gql`
  mutation DeleteVariation($id: ID!) {
    deleteAdVariation(id: $id)
  }
`;


export const CREATE_VARIATION_TYPE = gql`
  mutation CreateVariationType($input: CreateAdVariationTypeInput!) {
    createAdVariationType(input: $input) {
      id
      type
      leboncoinData
      typeValues {
        id
        value
        leboncoinData
      }
    }
  }
`;

export const CREATE_VARIATION_TYPE_VALUE = gql`
  mutation CreateVariationTypeValue($input: CreateAdVariationTypeValueInput!) {
    createAdVariationTypeValue(input: $input) {
      id
      value
      leboncoinData
      variations {
        id
      }
    }
  }
`;

export const UPDATE_VARIATION_TYPE = gql`
  mutation UpdateVariationType($id: ID!, $input: UpdateAdVariationTypeInput!) {
    updateAdVariationType(id: $id, input: $input) {
      id
      type
      leboncoinData
      typeValues {
        id
        value
        leboncoinData
      }
    }
  }
`;

export const UPDATE_VARIATION_TYPE_VALUE = gql`
  mutation UpdateVariationTypeValue($id: ID!, $input: UpdateAdVariationTypeValueInput!) {
    updateAdVariationTypeValue(id: $id, input: $input) {
      id
      value
      leboncoinData
    }
  }
`;


export const CREATE_AD_VARIATION = gql`
  mutation CreateAdVariation($input: CreateAdVariationInput!) {
    createAdVariation(input: $input) {
      id
      leboncoinData
      ebayData
    }
  }
`;

export const LINK_AD_VARIATION_TO_SUPPLIER_PRODUCT_VARIATION = gql`
  mutation LinkAdVariationToSupplierProductVariation($adVariationId: ID!, $supplierProductVariationKeys: [SupplierProductVariationCompositeKey!]!) {
    link_AdVariation_SupplierProductVariation(adVariationId: $adVariationId, supplierProductVariationKeys: $supplierProductVariationKeys) {
      id
    }
  }
`;

export const UNLINK_AD_VARIATION_FROM_SUPPLIER_PRODUCT_VARIATION = gql`
  mutation UnlinkAdVariationFromSupplierProductVariation($adVariationId: ID!, $supplierProductVariationKeys: [SupplierProductVariationCompositeKey!]!) {
    unlink_AdVariation_SupplierProductVariation(adVariationId: $adVariationId, supplierProductVariationKeys: $supplierProductVariationKeys) {
      id
    }
  }
`;

export const LINK_AD_VARIATION_TO_TYPE_VALUE = gql`
  mutation LinkAdVariationToTypeValue($adVariationId: ID!, $typeValueIds: [ID!]!) {
    link_AdVariation_TypeValue(adVariationId: $adVariationId, typeValueIds: $typeValueIds) {
      id
      variationTypeValues {
        id
        value
        type {
          id
          type
        }
      }
    }
  }
`;

export const UNLINK_AD_VARIATION_FROM_TYPE_VALUE = gql`
  mutation UnlinkAdVariationFromTypeValue($adVariationId: ID!, $typeValueIds: [ID!]!) {
    unlink_AdVariation_TypeValue(adVariationId: $adVariationId, typeValueIds: $typeValueIds) {
      id
      variationTypeValues {
        id
        value
        type {
          id
          type
        }
      }
    }
  }
`;

export const VARIATIONS_SECTION_FRAGMENT = gql`
  fragment VariationsSection_ad on Ad {
    id
    typeIdUsedForImages
    marginPercentOnBaseCost
    targetListingPriceIncludingVat
    listingPriceRoundingMethod
    variationTypes {
      id
      type
      leboncoinData
      typeValues {
        id
        value
        leboncoinData
        order
        medias {
          id
          url
        }
      }
    }
    variations {
      id
      leboncoinData
      ebayData
      marginPercentOnBaseCost
      targetListingPriceIncludingVat
      listingPriceRoundingMethod
      variationTypeValues {
        id
        value
        leboncoinData
        type {
          id
          type
          leboncoinData
        }
        medias {
          id
          url
        }
      }

      supplierProductVariations {
        sku
        supplierProductId
        supplierName
        data
        calculatedCost {
          destinationCountry
          totalCostIncludingVAT
          totalCostExcludingVAT
          totalCostVAT
          VATRate
        }
        availableStock {
          destinationCountry
          availableStock
        }
        packaging
      }
    }
    medias {
      id
      url
    }
    leboncoinData
    ebayData
  }
`;

export const DELETE_VARIATION_TYPE = gql`
  mutation VariationsSection_DeleteVariationType($id: ID!) {
    deleteAdVariationType(id: $id)
  }
`;

const GENERATE_VARIATIONS = gql`
  mutation VariationsSection_GenerateVariations($adId: ID!) {
    generateVariations(adId: $adId) {
      id
      variationTypeValues {
        id
        value
        type {
          id
          type
        }
      }
    }
  }
`;

const UPDATE_AD_VARIATION = gql`
  mutation UpdateAdVariation($id: ID!, $input: UpdateAdVariationInput!) {
    updateAdVariation(id: $id, input: $input) {
      id
      marginPercentOnBaseCost
      targetListingPriceIncludingVat
      listingPriceRoundingMethod
    }
  }
`;